import { Camera } from "@capacitor/camera";
import { Capacitor } from "@capacitor/core";
import { Filesystem } from "@capacitor/filesystem";
import {
  AndroidSettings,
  IOSSettings,
  NativeSettings,
} from "capacitor-native-settings";

export type PermissionState =
  | "granted"
  | "denied"
  | "prompt"
  | "prompt-with-rationale";
export type NativePlatform = "android" | "ios";

export const isNativeApp = (): boolean => Capacitor.isNativePlatform();

export const getNativePlatform = (): NativePlatform | null => {
  const p = Capacitor.getPlatform();
  if (p === "android" || p === "ios") return p;
  return null;
};

export const openAppSettings = async (): Promise<void> => {
  try {
    const platform = getNativePlatform();
    if (platform === "android") {
      await NativeSettings.openAndroid({
        option: AndroidSettings.ApplicationDetails,
      });
    } else if (platform === "ios") {
      await NativeSettings.openIOS({ option: IOSSettings.App });
    }
    // On web there are no app settings to open — the browser's
    // permission prompt is re-triggered by calling getUserMedia again.
  } catch (err) {
    console.error("[nativePermissions] openAppSettings error:", err);
  }
};

export const requestCameraPermission = async (): Promise<boolean> => {
  if (!isNativeApp()) {
    // Web: Use getUserMedia to trigger the browser's camera permission prompt.
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false,
      });
      stream.getTracks().forEach((t) => t.stop());
      return true;
    } catch (err) {
      console.warn("[nativePermissions] Web camera permission denied:", err);
      return false;
    }
  }
  try {
    const statusBefore = await Camera.checkPermissions();
    if (statusBefore.camera === "granted") return true;
    const statusAfter = await Camera.requestPermissions({
      permissions: ["camera"],
    });
    return statusAfter.camera === "granted";
  } catch (err) {
    console.warn("[nativePermissions] Camera permission check failed:", err);
    return true;
  }
};

export const requestPhotosPermission = async (): Promise<boolean> => {
  if (!isNativeApp()) return true;
  try {
    const statusBefore = await Camera.checkPermissions();
    if (statusBefore.photos === "granted" || statusBefore.photos === "limited")
      return true;
    const statusAfter = await Camera.requestPermissions({
      permissions: ["photos"],
    });
    return (
      statusAfter.photos === "granted" || statusAfter.photos === "limited"
    );
  } catch (err) {
    console.warn("[nativePermissions] Photos permission check failed:", err);
    return false;
  }
};

export const requestStoragePermission = async (): Promise<boolean> => {
  if (!isNativeApp()) return true;
  try {
    const platform = getNativePlatform();
    if (platform === "ios") return true;
    const statusBefore = await Filesystem.checkPermissions();
    if (statusBefore.publicStorage === "granted") return true;
    const statusAfter = await Filesystem.requestPermissions();
    return statusAfter.publicStorage === "granted";
  } catch (err) {
    console.warn("[nativePermissions] Storage permission check failed:", err);
    return true;
  }
};

export const requestImagePickerPermissions = async (): Promise<boolean> => {
  return requestCameraPermission();
};

export const requestFilePickerPermissions = async (): Promise<boolean> => {
  return requestCameraPermission();
};
